import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { formatCurrency } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/useAuth';
import { useQuery } from '@tanstack/react-query';
import { Search, Package, ArrowLeft } from 'lucide-react';

export interface CatalogLineItemData {
  description: string;
  quantity: number;
  base_unit: string;
  category_id: string;
  base_cost: number;
  markup_percentage: number;
  unit_cost: number;
  source_item_id: string;
  source_type: 'internal' | 'supplier';
}

interface InvoiceCatalogPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CatalogLineItemData) => void;
}

const InvoiceCatalogPicker: React.FC<InvoiceCatalogPickerProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { profile } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [quantity, setQuantity] = useState<number | ''>(1);

  const { data: internalItems = [] } = useQuery({
    queryKey: ['invoiceCatalogInternal', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const { data, error } = await supabase
        .from('internal_line_items')
        .select('id, description, title, base_cost, base_unit, markup_percentage, category_id, final_price, image_urls')
        .eq('company_id', profile.company_id)
        .eq('is_active', true)
        .order('title', { ascending: true });
      if (error) throw error;
      return (data || []).map((item) => ({ ...item, itemType: 'internal' as const }));
    },
    enabled: isOpen && !!profile?.company_id,
  });

  const { data: supplierItems = [] } = useQuery({
    queryKey: ['invoiceCatalogSupplier', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const { data, error } = await supabase
        .from('internal_line_items')
        .select('id, description, title, base_cost, base_unit, markup_percentage, category_id, final_price, image_urls')
        .eq('company_id', profile.company_id)
        .not('supplier_id', 'is', null)
        .eq('is_active', true)
        .order('title', { ascending: true });
      if (error) throw error;
      return (data || []).map((item) => ({ ...item, itemType: 'supplier' as const }));
    },
    enabled: isOpen && !!profile?.company_id,
  });

  const allItems = [...internalItems, ...supplierItems];

  const filteredItems = allItems.filter((item) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (item.title?.toLowerCase().includes(q)) ||
      (item.description?.toLowerCase().includes(q))
    );
  });

  const handleSelectItem = (item: any) => {
    setSelectedItem(item);
    setQuantity(1);
  };

  const handleConfirm = () => {
    if (!selectedItem || quantity === '' || quantity <= 0) return;

    const baseCost = selectedItem.base_cost || 0;
    const markup = selectedItem.markup_percentage || 0;
    const unitCost = selectedItem.final_price || baseCost * (1 + markup / 100);

    onSubmit({
      description: selectedItem.title || selectedItem.description,
      quantity: quantity as number,
      base_unit: selectedItem.base_unit || 'each',
      category_id: selectedItem.category_id || '',
      base_cost: baseCost,
      markup_percentage: markup,
      unit_cost: unitCost,
      source_item_id: selectedItem.id,
      source_type: selectedItem.itemType,
    });

    setSelectedItem(null);
    setQuantity(1);
    setSearchQuery('');
  };

  const handleClose = () => {
    setSelectedItem(null);
    setQuantity(1);
    setSearchQuery('');
    onClose();
  };

  const getTypeBadge = (type: string) => {
    const styles = type === 'internal'
      ? 'bg-blue-100 text-blue-800 border-blue-300'
      : 'bg-green-100 text-green-800 border-green-300';
    return <Badge variant="outline" className={styles}>{type}</Badge>;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[calc(100vw-16px)] sm:max-w-2xl max-h-[90vh] sm:max-h-[80vh] p-4 sm:p-6 flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {selectedItem ? 'Set Quantity' : 'Select from Catalog'}
          </DialogTitle>
        </DialogHeader>

        {selectedItem ? (
          <div className="space-y-4">
            <Button variant="ghost" size="sm" onClick={() => setSelectedItem(null)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to catalog
            </Button>

            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <h3 className="font-semibold">{selectedItem.title || selectedItem.description}</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-muted-foreground">Base Cost:</span> {formatCurrency(selectedItem.base_cost || 0)}</div>
                <div><span className="text-muted-foreground">Markup:</span> {selectedItem.markup_percentage || 0}%</div>
                <div><span className="text-muted-foreground">Unit Price:</span> {formatCurrency(selectedItem.final_price || selectedItem.base_cost * (1 + (selectedItem.markup_percentage || 0) / 100))}</div>
                <div><span className="text-muted-foreground">Unit:</span> {selectedItem.base_unit || 'each'}</div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="catalog-qty">Quantity *</Label>
              <Input
                id="catalog-qty"
                type="number"
                step="0.01"
                min="0.01"
                value={quantity}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') setQuantity('');
                  else {
                    const num = parseFloat(val);
                    if (!isNaN(num) && num >= 0) setQuantity(num);
                  }
                }}
              />
            </div>

            {quantity !== '' && quantity > 0 && (
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex justify-between text-lg font-bold">
                  <span>Total:</span>
                  <span className="text-primary">
                    {formatCurrency(
                      (quantity as number) *
                      (selectedItem.final_price || selectedItem.base_cost * (1 + (selectedItem.markup_percentage || 0) / 100))
                    )}
                  </span>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSelectedItem(null)}>Cancel</Button>
              <Button onClick={handleConfirm} disabled={quantity === '' || quantity <= 0}>
                Add to Invoice
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4 overflow-hidden">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search catalog items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="overflow-y-auto flex-1 space-y-2 max-h-[50vh]">
              {filteredItems.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <p className="text-lg font-medium">No items found</p>
                  <p className="text-sm">Try adjusting your search</p>
                </div>
              ) : (
                filteredItems.map((item) => {
                  const price = item.final_price || item.base_cost || 0;
                  return (
                    <Card
                      key={`${item.itemType}-${item.id}`}
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => handleSelectItem(item)}
                    >
                      <CardContent className="p-2 sm:p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-muted rounded flex items-center justify-center flex-shrink-0 text-lg">
                            {item.image_urls?.[0] ? (
                              <img src={item.image_urls[0]} alt="" className="w-full h-full object-cover rounded" />
                            ) : '📦'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-sm sm:text-base line-clamp-2 break-words">{item.title || item.description}</span>
                            <div className="flex items-center gap-2 mt-0.5">
                              {getTypeBadge(item.itemType)}
                              <span className="text-xs sm:text-sm text-muted-foreground">per {item.base_unit || 'each'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="font-bold text-primary whitespace-nowrap self-end sm:self-auto sm:ml-auto">
                          {formatCurrency(price)}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceCatalogPicker;
